import React, { useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useStore } from 'react-redux';

import { createPaymentWidgetRemote } from 'components/vendor-form-renderer/form-renderer-remote';
import { useLocale } from 'hooks/use-locale.hook';
import { configSelector, selectAccessToken } from 'store/config/selectors';
import { RootState } from 'store/reducer';
import { selectBrandTheme } from 'store/theme/selectors';

import { PaymentResult, PaymentWidgetProps } from './types';
import { PaymentUnavailable } from './styles';

/*
 * ⛔ The caller owns the failure UI, by the remote factory's own contract: a form that fails to load
 * can be skipped, a payment cannot. Rendering nothing would leave a tenant on a screen with no way to
 * pay and nothing saying why. The add-ons are already ordered by this point — the charge is what is
 * outstanding — so the copy says the form is unavailable rather than that anything was lost.
 *
 * ⚠️ Module scope, not a `useMemo` inside the component. Defining it in the render gives React a new
 * component type every pass, which tears down and rebuilds the subtree — and the subtree here is a
 * federated payment sheet.
 */
const PaymentUnavailableFallback = (): JSX.Element => {
  const intl = useIntl();

  return (
    <PaymentUnavailable role="alert" data-testid="add-on-payment-unavailable">
      {intl.formatMessage({ id: 'add_on_payment_unavailable' })}
    </PaymentUnavailable>
  );
};

interface Props {
  /** The session billing opened against the linked request. No session, no payment screen. */
  checkoutSessionUuid: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPaymentComplete?: (result: PaymentResult) => void;
}

/*
 * RES-1336 — the tenant pays for their add-ons without leaving the booking flow.
 *
 * ⛔ THIS COMPONENT RENDERS NO PAYMENT UI OF ITS OWN, and must not grow any. Everything inside the
 * sheet — saved cards, gateway tokenization, 3-D Secure, the line items, tax, the pay control and the
 * result stream — belongs to billing-admin's widget, which is the same one the tenant Service Request
 * app loads. This app hands it a session and acts on the callback. Reimplementing any of it here
 * would be a second payment surface to keep in step with billing's, which is the thing RES-1336 set
 * out to avoid.
 *
 * ⚠️ The widget is its OWN overlay — a ui-atoms Dialog on desktop, a Drawer on mobile — portaled to
 * `document.body`. It is therefore a DETOUR from the step, not a screen inside it: it opens on top of
 * whatever is showing and cannot take part in the step's own back/arrow navigation. `open` and
 * `onOpenChange` are how that detour is driven.
 */
export const AddOnPayment = ({ checkoutSessionUuid, open, onOpenChange, onPaymentComplete }: Props): JSX.Element => {
  const store = useStore<RootState>();
  const locale = useLocale();
  const { apiUrl } = configSelector(store.getState());
  const brand = selectBrandTheme(store.getState());

  /* Read through the store, not a closure: a token captured at mount would 401 silently after a refresh. */
  const getAuthToken = useCallback(() => Promise.resolve(selectAccessToken(store.getState()) ?? ''), [store]);

  /* Created once: rebuilding it in the render remounts the widget, which would drop a half-entered card. */
  const RemotePaymentWidget = useMemo(() => createPaymentWidgetRemote(PaymentUnavailableFallback), []);

  /*
   * ⛔ THE BODY IS UNLOCKED BY HAND ON THE WAY OUT, and this is not belt and braces.
   *
   * A modal Radix layer sets `document.body.style.pointerEvents = 'none'` and restores whatever it
   * found there when it closes. That contract holds for ONE copy of the library. This app runs
   * several: three versions of `@radix-ui/react-dismissable-layer` resolve inside the bundle, and the
   * widget below brings a fourth of its own across the module-federation boundary -- the one copy we
   * cannot dedupe from here at all. Each has its own module-level "what was there before" and its own
   * layer registry, and they all write the same one global property, so a copy that locks while
   * another already holds the lock captures `none` as the original and restores `none` on the way
   * out. The page is then inert with no sheet on screen, which is what a tenant reports as "the app
   * stopped responding after I closed the add-ons".
   *
   * ⭐ HERE AND NOT ONLY ON `AddOnsDialog`, which has carried the same effect since RES-1336. The
   * checkout step opens this inside that dialog and inherits its cleanup; booking-details opens it on
   * its own, with no dialog of ours anywhere above it, and got none. Same fault, a surface the first
   * mitigation never covered -- so it belongs to the payment sheet itself, which is the piece common
   * to both.
   *
   * ⚠️ TWICE, because the ORDER IS NOT OURS TO KNOW. React destroys a deleted subtree's effects from
   * the top down, so this can run either side of the widget's own restore depending on where the
   * federated layer sits; the deferred pass lands after every copy in the commit has had its turn.
   * Both are guarded and idempotent, so whichever one is the real fix, the other is a no-op.
   *
   * ⚠️ GUARDED ON WHAT IS ACTUALLY LEFT, never unconditional. If any dialog is still open -- an alert
   * over the top of the sheet, a confirmation outliving it -- the lock is still wanted, and clearing
   * it would hand clicks to a page that is meant to be behind a modal. Only when nothing remains is an
   * inert body certainly wrong.
   *
   * ⚠️ NOT REPRODUCED IN JSDOM: a single copy of the library cleans up after itself correctly there,
   * which is the evidence that the fault is in having several rather than in the close path. This is a
   * floor under the symptom, not a diagnosis of a sequence. The real fix is one copy, and that one is
   * shared-packages' to make.
   */
  useEffect(() => {
    const unlockIfNothingIsOpen = () => {
      const anotherLayerIsOpen = document.querySelector(
        '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]',
      );

      if (!anotherLayerIsOpen && document.body.style.pointerEvents === 'none') {
        document.body.style.pointerEvents = '';
      }
    };

    return () => {
      unlockIfNothingIsOpen();
      window.setTimeout(unlockIfNothingIsOpen, 0);
    };
  }, []);

  const props: PaymentWidgetProps = {
    /* Must be absolute: the remote runs `new URL(apiUrl).origin`, and a relative URL throws inside it. */
    apiUrl,
    getAuthToken,
    locale,
    /*
     * The building's brand, as two colour strings. The widget scopes them to its own container and
     * cannot read a theme provider across the federation boundary — see the note on `PaymentWidgetProps`.
     */
    theme: { primaryColor: brand?.primary_color, primaryFontColor: brand?.primary_font_color },
    checkoutSessionUuid,
    open,
    onOpenChange,
    onPaymentComplete,
  };

  /*
   * Spread, never `props={props}`: the bridge forwards by rest on both sides and reads no key named
   * `props`, so nesting leaves the remote with `apiUrl` undefined — RES-1325, on the form renderer,
   * was exactly this. Keep the keys unconditional (the bridge's effect deps are `Object.values`) and
   * off the names it consumes itself: moduleName, memoryRoute, basename, providerInfo, className,
   * style, fallback, loading, exportName.
   */
  // eslint-disable-next-line react/jsx-props-no-spreading
  return <RemotePaymentWidget {...props} />;
};
